import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  User,
  Bell,
  Map,
  Palette,
  Globe,
  Gauge,
  Clock,
  Shield,
  Save,
  RotateCcw,
  Camera,
  CalendarClock
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchDevices } from "@/lib/traccar-api";
import { ScheduledReportsDialog } from "@/components/reports/ScheduledReportsDialog";

interface AppSettings {
  theme: "light" | "dark" | "system";
  language: string;
  distanceUnit: "km" | "mi";
  speedUnit: "kmh" | "mph" | "knots";
  defaultMapStyle: string;
  showClustersByDefault: boolean;
  mapRefreshInterval: number;
  enableSoundAlerts: boolean;
  enableDesktopNotifications: boolean;
  notifyOnDeviceOffline: boolean;
  notifyOnGeofenceEntry: boolean;
  notifyOnGeofenceExit: boolean;
  notifyOnOverspeed: boolean;
  sessionTimeout: number;
  autoRefreshData: boolean;
  hikBaseUrl: string;
  hikUser: string;
  hikPassword: string;
  hikTrackID: number;
  hikWorkerUrl: string;
  hikVideoBaseUrl: string;
}

const DEFAULT_SETTINGS: AppSettings = {
  theme: "system",
  language: "en",
  distanceUnit: "km",
  speedUnit: "kmh",
  defaultMapStyle: "streets",
  showClustersByDefault: true,
  mapRefreshInterval: 30,
  enableSoundAlerts: false,
  enableDesktopNotifications: true,
  notifyOnDeviceOffline: true,
  notifyOnGeofenceEntry: true,
  notifyOnGeofenceExit: true,
  notifyOnOverspeed: true,
  sessionTimeout: 60,
  autoRefreshData: true,
  hikBaseUrl: "https://gateway.jsrastreamento.com",
  hikUser: "",
  hikPassword: "",
  hikTrackID: 101,
  hikWorkerUrl: "",
  hikVideoBaseUrl: "https://video.jsrastreamento.com",
};

const Settings = () => {
  const { t, i18n } = useTranslation();

  const [scheduledOpen, setScheduledOpen] = useState(false);
  const { data: devices = [] } = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });

  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem("app_settings");
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("app_settings");
    const savedSettings = saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
    setHasChanges(JSON.stringify(settings) !== JSON.stringify(savedSettings));
  }, [settings]);

  const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));

    // Apply language change immediately for preview
    if (key === "language" && typeof value === "string") {
      i18n.changeLanguage(value);
    }
  };

  const saveSettings = () => {
    localStorage.setItem("app_settings", JSON.stringify(settings));

    // Apply theme
    if (settings.theme === "dark") {
      document.documentElement.classList.add("dark");
    } else if (settings.theme === "light") {
      document.documentElement.classList.remove("dark");
    } else {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }

    // Apply language
    i18n.changeLanguage(settings.language);

    setHasChanges(false);
    toast.success(t("settings.saved"));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    i18n.changeLanguage(DEFAULT_SETTINGS.language);
    toast.info(t("settings.resetToDefaults"));
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        updateSetting("enableDesktopNotifications", true);
        toast.success(t("settings.notificationsEnabled"));
      } else {
        toast.error(t("settings.notificationsDenied"));
      }
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h1 className="text-2xl font-bold">{t("settings.title")}</h1>
            <p className="text-muted-foreground">{t("settings.subtitle")}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetSettings} disabled={!hasChanges}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t("common.reset")}
            </Button>
            <Button onClick={saveSettings} disabled={!hasChanges}>
              <Save className="h-4 w-4 mr-2" />
              {t("common.save")}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <Tabs defaultValue="display" className="space-y-6">
            <TabsList className="grid w-full max-w-2xl grid-cols-6">
              <TabsTrigger value="display" className="gap-2">
                <Palette className="h-4 w-4" />
                <span className="hidden sm:inline">{t("settings.tabs.display")}</span>
              </TabsTrigger>
              <TabsTrigger value="map" className="gap-2">
                <Map className="h-4 w-4" />
                <span className="hidden sm:inline">{t("settings.tabs.map")}</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="gap-2">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">{t("settings.tabs.alerts")}</span>
              </TabsTrigger>
              <TabsTrigger value="account" className="gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{t("settings.tabs.account")}</span>
              </TabsTrigger>
              <TabsTrigger value="cameras" className="gap-2">
                <Camera className="h-4 w-4" />
                <span className="hidden sm:inline">{t("settings.tabs.cameras")}</span>
              </TabsTrigger>
              <TabsTrigger value="scheduled" className="gap-2">
                <CalendarClock className="h-4 w-4" />
                <span className="hidden sm:inline">{t("settings.tabs.scheduled")}</span>
              </TabsTrigger>
            </TabsList>

            {/* Display Settings */}
            <TabsContent value="display" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    {t("settings.appearance.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.appearance.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.appearance.theme")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.appearance.themeDesc")}</p>
                    </div>
                    <Select
                      value={settings.theme}
                      onValueChange={(v) => updateSetting("theme", v as AppSettings["theme"])}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">{t("settings.appearance.light")}</SelectItem>
                        <SelectItem value="dark">{t("settings.appearance.dark")}</SelectItem>
                        <SelectItem value="system">{t("settings.appearance.system")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        {t("settings.appearance.language")}
                      </Label>
                      <p className="text-sm text-muted-foreground">{t("settings.appearance.languageDesc")}</p>
                    </div>
                    <Select
                      value={settings.language}
                      onValueChange={(v) => updateSetting("language", v)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="pt">Português (PT)</SelectItem>
                        <SelectItem value="pt-BR">Português (BR)</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="h-5 w-5" />
                    {t("settings.units.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.units.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.units.distance")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.units.distanceDesc")}</p>
                    </div>
                    <Select
                      value={settings.distanceUnit}
                      onValueChange={(v) => updateSetting("distanceUnit", v as AppSettings["distanceUnit"])}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="km">{t("settings.units.kilometers")}</SelectItem>
                        <SelectItem value="mi">{t("settings.units.miles")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.units.speed")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.units.speedDesc")}</p>
                    </div>
                    <Select
                      value={settings.speedUnit}
                      onValueChange={(v) => updateSetting("speedUnit", v as AppSettings["speedUnit"])}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kmh">{t("settings.units.kmh")}</SelectItem>
                        <SelectItem value="mph">{t("settings.units.mph")}</SelectItem>
                        <SelectItem value="knots">{t("settings.units.knots")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Map Settings */}
            <TabsContent value="map" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Map className="h-5 w-5" />
                    {t("settings.mapPrefs.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.mapPrefs.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.mapPrefs.defaultStyle")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.mapPrefs.defaultStyleDesc")}</p>
                    </div>
                    <Select
                      value={settings.defaultMapStyle}
                      onValueChange={(v) => updateSetting("defaultMapStyle", v)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="streets">{t("map.light")}</SelectItem>
                        <SelectItem value="dark">{t("map.dark")}</SelectItem>
                        <SelectItem value="voyager">{t("map.voyager")}</SelectItem>
                        <SelectItem value="google-roadmap">{t("map.googleRoads")}</SelectItem>
                        <SelectItem value="google-satellite">{t("map.googleSatellite")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.mapPrefs.clusters")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.mapPrefs.clustersDesc")}</p>
                    </div>
                    <Switch
                      checked={settings.showClustersByDefault}
                      onCheckedChange={(v) => updateSetting("showClustersByDefault", v)}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notification Settings */}
            <TabsContent value="notifications" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    {t("settings.alertPrefs.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.alertPrefs.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.alertPrefs.soundAlerts")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.alertPrefs.soundAlertsDesc")}</p>
                    </div>
                    <Switch
                      checked={settings.enableSoundAlerts}
                      onCheckedChange={(v) => updateSetting("enableSoundAlerts", v)}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.alertPrefs.desktopNotifications")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.alertPrefs.desktopNotificationsDesc")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!settings.enableDesktopNotifications && (
                        <Button variant="outline" size="sm" onClick={requestNotificationPermission}>
                          {t("settings.alertPrefs.enable")}
                        </Button>
                      )}
                      <Switch
                        checked={settings.enableDesktopNotifications}
                        onCheckedChange={(v) => updateSetting("enableDesktopNotifications", v)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("settings.eventNotifications.title")}</CardTitle>
                  <CardDescription>{t("settings.eventNotifications.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>{t("settings.eventNotifications.deviceOffline")}</Label>
                    <Switch
                      checked={settings.notifyOnDeviceOffline}
                      onCheckedChange={(v) => updateSetting("notifyOnDeviceOffline", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{t("settings.eventNotifications.geofenceEntry")}</Label>
                    <Switch
                      checked={settings.notifyOnGeofenceEntry}
                      onCheckedChange={(v) => updateSetting("notifyOnGeofenceEntry", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{t("settings.eventNotifications.geofenceExit")}</Label>
                    <Switch
                      checked={settings.notifyOnGeofenceExit}
                      onCheckedChange={(v) => updateSetting("notifyOnGeofenceExit", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{t("settings.eventNotifications.overspeed")}</Label>
                    <Switch
                      checked={settings.notifyOnOverspeed}
                      onCheckedChange={(v) => updateSetting("notifyOnOverspeed", v)}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Settings */}
            <TabsContent value="account" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    {t("settings.session.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.session.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {t("settings.session.timeout")}
                      </Label>
                      <p className="text-sm text-muted-foreground">{t("settings.session.timeoutDesc")}</p>
                    </div>
                    <Select
                      value={settings.sessionTimeout.toString()}
                      onValueChange={(v) => updateSetting("sessionTimeout", parseInt(v))}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 {t("settings.session.minutes")}</SelectItem>
                        <SelectItem value="30">30 {t("settings.session.minutes")}</SelectItem>
                        <SelectItem value="60">{t("settings.session.hour")}</SelectItem>
                        <SelectItem value="480">{t("settings.session.hours")}</SelectItem>
                        <SelectItem value="0">{t("settings.session.never")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("settings.session.autoRefresh")}</Label>
                      <p className="text-sm text-muted-foreground">{t("settings.session.autoRefreshDesc")}</p>
                    </div>
                    <Switch
                      checked={settings.autoRefreshData}
                      onCheckedChange={(v) => updateSetting("autoRefreshData", v)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {t("settings.accountInfo.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.accountInfo.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>{t("settings.accountInfo.userData")}</Label>
                      <Input
                        value={localStorage.getItem("traccar_user") ? JSON.parse(localStorage.getItem("traccar_user") || "{}").name || "User" : t("settings.accountInfo.notAvailable")}
                        disabled
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("settings.accountInfo.managedByServer")}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Cameras / Hikvision Settings */}
            <TabsContent value="cameras" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    {t("settings.hikvision.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.hikvision.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("settings.hikvision.baseUrl")}</Label>
                    <Input
                      placeholder="https://gateway.jsrastreamento.com"
                      value={settings.hikBaseUrl || 'https://hikvision.entrack-plataforma.workers.dev'}
                      onChange={(e) => updateSetting("hikBaseUrl", e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">{t("settings.hikvision.baseUrlDesc")}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("settings.hikvision.workerUrl")}</Label>
                    <Input
                      placeholder="https://gateway.jsrastreamento.com/hik-proxy"
                      value={settings.hikWorkerUrl}
                      onChange={(e) => updateSetting("hikWorkerUrl", e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">{t("settings.hikvision.workerUrlDesc")}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("settings.hikvision.videoBaseUrl")}</Label>
                    <Input
                      placeholder="https://video.jsrastreamento.com"
                      value={settings.hikVideoBaseUrl}
                      onChange={(e) => updateSetting("hikVideoBaseUrl", e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">{t("settings.hikvision.videoBaseUrlDesc")}</p>
                  </div>
                  <Separator />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>{t("settings.hikvision.user")}</Label>
                      <Input
                        placeholder="admin"
                        value={settings.hikUser || 'admin'}
                        onChange={(e) => updateSetting("hikUser", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("settings.hikvision.password")}</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={settings.hikPassword || '1937armada'}
                        onChange={(e) => updateSetting("hikPassword", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("settings.hikvision.defaultTrackID")}</Label>
                    <Input
                      type="number"
                      placeholder="101"
                      value={settings.hikTrackID}
                      onChange={(e) => updateSetting("hikTrackID", parseInt(e.target.value) || 101)}
                      className="w-32"
                    />
                    <p className="text-xs text-muted-foreground">{t("settings.hikvision.defaultTrackIDDesc")}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="scheduled" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarClock className="h-5 w-5" />
                    {t("reports.scheduled.title")}
                  </CardTitle>
                  <CardDescription>{t("settings.scheduled.subtitle")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setScheduledOpen(true)}>
                    <CalendarClock className="h-4 w-4 mr-2" />
                    {t("settings.scheduled.manage")}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <ScheduledReportsDialog open={scheduledOpen} onOpenChange={setScheduledOpen} devices={devices} />
    </DashboardLayout>
  );
};

export default Settings;
